import { FileConfig } from './../../envelope/model/file-config';
import { Settings } from './settings';
import { Signatory } from './signatory';
import { Authorizer } from './authorizer';
import { ObserverList } from './observer';
import { Responsible } from './responsible';
import { EnvelopeFileIds } from './envelope-file-ids.model';
import { EnvelopeSignatoryStamps } from './envelope-signatory-stamps';
import { EnvelopeSignatoryRubrics } from './envelope-signatory-rubrics';
import { EnvelopeSignatoryCustomFields } from './envelope-signatory-custom-fields';
import { PageUtil } from 'src/app/util/page-util';
import { WhenWillNotified } from 'src/app/enum/when-will-notified';
import { CustomFieldTypeEnum } from 'src/app/enum/custom-field-type.enum';

const signatureStampHeightInMm = 22.39;
const rubricStampHeightInMn = 12.33;

export class MainEnvelope {
  settings: Settings;
  signatories: Signatory;
  authorizers: Authorizer;
  observers: ObserverList;
  responsibles: Responsible;
  envelopeFiles: EnvelopeFileIds;
  suffixBucketName: string;
  envelopeSignatoryStamps: EnvelopeSignatoryStamps;
  envelopeSignatoryRubrics: EnvelopeSignatoryRubrics;
  envelopeSignatoryCustomFields: EnvelopeSignatoryCustomFields;
  fileConfig: FileConfig[];

  generateSend(): MainEnvelopeToSend {
    const signatories: MainEnvelopeToSendSignatories[] = [];
    const authorizers: MainEnvelopeToSendAuthorizer[] = [];
    const observers: MainEnvelopeToSendObservers[] = [];
    const responsibles: MainEnvelopeToSendResponsible[] = [];
    const files: MainEnvelopeToSendFiles[] = [];
    let envelopeSignatoryRubricsFiles = [];

    for (const signatory of this.signatories.signatoryList) {
      const envelopeSignatoryFiles = this.envelopeSignatoryStamps.stampList
        .filter(x => x.signatoryId === signatory.id.toString())
        .map(y => ({
          envelopeFileId: this.envelopeFiles.envelopeFiles
            .find(x => this.removeExt(x.fileName) === y.fileName).id,
          signaturePositionX: y.signaturePositionX,
          signaturePositionY: PageUtil.recalculatesCoordinatesForBry(this.fileConfig.find(x => x.fileName === y.fileName && x.page === y.signaturePositionPage).height, y.signaturePositionY, y.signatureSizeHeight != null ? y.signatureSizeHeight : signatureStampHeightInMm),
          signaturePositionPage: y.signaturePositionPage,
          signatureSizeWidth: y.signatureSizeWidth,
          signatureSizeHeight: y.signatureSizeHeight
        }));

      if (this.envelopeSignatoryRubrics !== undefined) {
        envelopeSignatoryRubricsFiles = this.envelopeSignatoryRubrics.rubricList
          .filter(x => x.signatoryId === signatory.id.toString())
          .map(y => ({
            envelopeFileId: this.envelopeFiles.envelopeFiles
              .find(x => this.removeExt(x.fileName) === y.fileName).id,
            rubricPositionX: y.positionX,
            rubricPositionY: PageUtil.recalculatesCoordinatesForBry(this.fileConfig.find(x => x.fileName === y.fileName && x.page === y.page).height, y.positionY, y.sizeHeight != null ? y.sizeHeight : rubricStampHeightInMn),
            rubricPositionPage: y.page,
            rubricSizeWidth: y.sizeWidth,
            rubricSizeHeight: y.sizeHeight
          }));
      }

      signatories.push({
        signatureType: signatory.signatureType.id,
        subscriberType: signatory.subscriberType.id,
        signatoryTypeRoleId: signatory.signatoryTypeRoleId,
        order: signatory.order,
        sendWhatsApp: signatory.sendWhatsApp,
        envelopeSignatoryFiles,
        envelopeSignatoryRubricsFiles,
        envelopeSignatoryCustomFieldFiles: [],
        signatory: {
          id: signatory.id,
          name: signatory.name,
          ssn: signatory.ssn,
          mail: signatory.mail,
          active: signatory.active,
          signatureType: signatory.signatureType.id,
          subscriberType: signatory.subscriberType.id,
        }
      });
    }

    if (this.authorizers && this.authorizers.authorizerList) {
      for (const authorizer of this.authorizers.authorizerList) {
        authorizers.push({
          id: authorizer.id,
          name: authorizer.name,
          ssn: authorizer.ssn,
          mail: authorizer.mail,
          active: authorizer.active,
          sendWhatsApp: authorizer.sendWhatsApp,
          phoneNumber: authorizer.phoneNumber
        });
      }
    }

    if (this.responsibles && this.responsibles.responsibleList) {
      for (const responsible of this.responsibles.responsibleList) {
        let envelopeSignatoryCustomFieldFiles = [];

        if (this.envelopeSignatoryCustomFields !== undefined) {
          envelopeSignatoryCustomFieldFiles = this.envelopeSignatoryCustomFields.customFieldList
            .filter((x) => x.signatoryId === responsible.id.toString())
            .map((y) => ({
              envelopeFileId: this.envelopeFiles.envelopeFiles
                .find((x) => this.removeExt(x.fileName) === y.fileName).id,
              positionX: y.positionX,
              positionY: PageUtil.recalculatesCoordinatesForBry(
                this.fileConfig.find((x) => x.fileName === y.fileName && x.page === y.page).height,
                y.positionY,
                0
              ),
              positionPage: y.page,
              displayName: y.displayName,
              fontSize: y.fontSize,
              required: y.required,
              sizeWidth: y.sizeWidth,
              sizeHeight: y.sizeHeight,
              fieldType: PageUtil.setCustomFieldTypeInt(y.fieldType as CustomFieldTypeEnum)
            }));
        }

        responsibles.push({
          id: responsible.id,
          name: responsible.name,
          mail: responsible.mail,
          active: true,
          sendWhatsApp: responsible.sendWhatsApp,
          phoneNumber: responsible.phoneNumber,
          envelopeSignatoryCustomFieldFiles
        });
      }
    }

    if (this.observers && this.observers.observerList) {
      for (const observer of this.observers.observerList) {
        observers.push({
          sendWhatsApp: observer.sendWhatsApp,
          whenNotified: observer.whenWillNotified,
          sendCopyFinishedEnvelope: observer.sendCopyFinishedEnvelope,
          signatory: {
            id: observer.id,
            mail: observer.mail,
            name: observer.name,
            phoneNumber: observer.phoneNumber,
            sendWhatsApp: observer.sendWhatsApp,
            ssn: observer.ssn
          }
        });
      }
    }

    for (const envelopeFile of this.envelopeFiles.envelopeFiles) {
      files.push({
        envelopeFileId: envelopeFile.id
      });
    }

    const toSend = new MainEnvelopeToSend();
    toSend.title = this.settings.title,
      toSend.isOrdered = this.settings.isOrdered,
      toSend.language = this.settings.language,
      toSend.message = this.settings.message,
      toSend.reminderFrequency = this.settings.reminderFrequency,
      toSend.segmentId = this.settings.segmentId,
      toSend.accessAuthentication = this.settings.accessAuthentication,
      toSend.authorizerAccessAuthentication = this.settings.authorizerAccessAuthentication,
      toSend.responsibleAccessAuthentication = this.settings.responsibleAccessAuthentication;
      toSend.verificationCode = this.suffixBucketName, //this.settings.verificationCode,
      toSend.expiration = this.settings.expiration,
      toSend.signatories = signatories,
      toSend.authorizers = authorizers;
      toSend.observers = observers;
      toSend.responsibles = responsibles;
    toSend.files = files;
    toSend.IsRejectable = this.settings.isRejectable;

    return toSend;
  }

  removeExt(fileName: string) {
    return fileName.split('.').slice(0, -1).join('.');
  }

  addSettings(settings: any) {
    this.settings = {
      title: settings.titleEnvelope,
      message: settings.message,
      language: settings.language.id,
      expiration: (settings.expiration) ? settings.expiration.singleDate.jsDate : undefined,
      segmentId: typeof settings.segment === 'object' ? settings.segment.id : null,
      accessAuthentication: false,
      authorizerAccessAuthentication: false,
      responsibleAccessAuthentication: false,
      isOrdered: false,
      reminderFrequency: 0,
      isRejectable: settings.isRejectable
    };
  }

  addSignatory(signatory: any): void {
    this.signatories = {
      signatoryList: signatory.elements
    };
    this.updateEnvelopeFromSignatoryForm(signatory);
  }

  addAuthorizers(signatory: any): void {
    this.authorizers = {
      authorizerList: signatory.envelopeAuthorizers
    };
  }

  addObservers(signatory: any): void {
    this.observers = {
      observerList: signatory.envelopeObserver
    }
  }

  addResponsibles(signatory: any): void {
    this.responsibles = {
      responsibleList: signatory.envelopeResponsible
    };
  }

  addSignatoryStamps(stamps: any): void {
    this.envelopeSignatoryStamps = {
      stampList: stamps
    };
  }

  addSignatoryRubrics(rubrics: any): void {
    this.envelopeSignatoryRubrics = {
      rubricList: rubrics
    };
  }

  addSignatoryCustomFields(customFields: any): void {
    this.envelopeSignatoryCustomFields = {
      customFieldList: customFields
    };
  }

  addFilesAndSuffixBucketName(envelopeFiles: any, fileConfig: FileConfig[], suffixBucketName: any): void {
    this.envelopeFiles = {
      envelopeFiles
    };
    this.fileConfig = fileConfig;
    this.suffixBucketName = suffixBucketName;
  }

  private updateEnvelopeFromSignatoryForm(signatory: any) {
    this.settings.accessAuthentication = signatory.accessAuthentication;
    this.settings.reminderFrequency = signatory.reminderFrequency.id;
    this.settings.isOrdered = signatory.isOrdered;
    this.settings.authorizerAccessAuthentication = signatory.authorizerAccessAuthentication;
    this.settings.responsibleAccessAuthentication = signatory.responsibleAccessAuthentication;
  }
}

export class MainEnvelopeToSend {
  title: string;
  isOrdered: boolean;
  IsRejectable: boolean;
  language: number;
  message: string;
  reminderFrequency: number;
  segmentId: number;
  accessAuthentication: boolean;
  authorizerAccessAuthentication: boolean;
  responsibleAccessAuthentication: boolean;
  verificationCode: string;
  expiration: string;
  hasFileAlreadySigned: boolean = false;
  signatories: MainEnvelopeToSendSignatories[];
  authorizers: MainEnvelopeToSendAuthorizer[];
  observers: MainEnvelopeToSendObservers[];
  responsibles: MainEnvelopeToSendResponsible[];
  files: MainEnvelopeToSendFiles[];
}

export class MainEnvelopeToSendSignatories {
  signatureType: number;
  subscriberType: number;
  signatoryTypeRoleId: number;
  order: number;
  signatory: MainEnvelopeToSendSignatory;
  sendWhatsApp: boolean;
  envelopeSignatoryFiles: MainEnvelopeToSendSignatoryStamp[];
  envelopeSignatoryRubricsFiles: MainEnvelopeToSendSignatoryRubrics[];
  envelopeSignatoryCustomFieldFiles: MainEnvelopeToSendSignatoryCustomFields[];
}

export class MainEnvelopeToSendObservers {
  sendWhatsApp: boolean;
  whenNotified: WhenWillNotified;
  sendCopyFinishedEnvelope: boolean;
  signatory: MainEnvelopeToSendObserver;
}

export class MainEnvelopeToSendSignatoryStamp {
  envelopeFileId: number;
  signaturePositionX: number;
  signaturePositionY: number;
  signaturePositionPage: string;
  signatureSizeWidth: number;
  signatureSizeHeight: number;
}

export class MainEnvelopeToSendSignatoryRubrics {
  envelopeFileId: number;
  rubricPositionX: number;
  rubricPositionY: number;
  rubricPositionPage: string;
  rubricSizeWidth: number;
  rubricSizeHeight: number;
}

export class MainEnvelopeToSendSignatoryCustomFields {
  envelopeFileId: number;
  positionX: number;
  positionY: number;
  positionPage: string;
  displayName: string;
  fontSize: number;
  required: boolean;
  sizeWidth: number;
  sizeHeight: number;
  fieldType: number;
}

export class MainEnvelopeToSendSignatory {
  id: number;
  name: string;
  ssn: string;
  mail: string;
  active: boolean;
  signatureType: number;
  subscriberType: number;
}

export class MainEnvelopeToSendObserver {
  id: number;
  name: string;
  ssn: string;
  mail: string;
  sendWhatsApp: boolean;
  phoneNumber: string;
}

export class MainEnvelopeToSendAuthorizer {
  id: number;
  name: string;
  ssn: string;
  mail: string;
  active: boolean;
  phoneNumber: string;
  sendWhatsApp: boolean;
}

export class MainEnvelopeToSendResponsible {
  id: number;
  name: string;
  mail: string;
  active: boolean;
  phoneNumber: string;
  sendWhatsApp: boolean;
  envelopeSignatoryCustomFieldFiles: MainEnvelopeToSendSignatoryCustomFields[];
}

export class MainEnvelopeToSendFiles {
  envelopeFileId: number;
}
